import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../../utils/authService';
import './FilterPage.css';

interface Project {
  project_id: number;
  batch_project_id: number;
  batch_project_name: string;
}

interface FiltersData {
  courses: string[];
  batches: {
    [course: string]: string[];
  };
  subjects: {
    [course: string]: {
      [batch: string]: string[];
    };
  };
  projects?: {
    [course: string]: {
      [batch: string]: Project[];
    };
  };
}

const FilterPage: React.FC = () => {
  const navigate = useNavigate();
  
  // Authentication check - redirect to login if not authenticated as Coordinator
  useEffect(() => {
    if (!sessionStorage.getItem("Coordinator")) {
      navigate('/');
      return;
    }
  }, [navigate]);

  const [filtersData, setFiltersData] = useState<FiltersData | null>(null);
  const [selectedCourse, setSelectedCourse] = useState<string>('');
  const [selectedBatch, setSelectedBatch] = useState<string>('');
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFilters();
  }, []);

  const fetchFilters = async () => {
    try {
      const userId = sessionStorage.getItem('Id');
      if (!userId) {
        console.error('User ID not found in session storage');
        return;
      }
      const response = await authService.get(`coordinator/filters/${userId}/`);
      setFiltersData(response.data);
      
      // Set default selections for course and batch only
      if (response.data.courses.length > 0) {
        setSelectedCourse(response.data.courses[0]);
        if (response.data.batches[response.data.courses[0]]?.length > 0) {
          setSelectedBatch(response.data.batches[response.data.courses[0]][0]);
        }
      }
    } catch (error) {
      console.error('Error fetching filters:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCourseChange = (course: string) => {
    setSelectedCourse(course);
    setSelectedBatch('');
    setSelectedSubject('');
    setSelectedProject(null);
    
    // Set default batch for new course only
    if (filtersData && filtersData.batches[course] && filtersData.batches[course].length > 0) {
      setSelectedBatch(filtersData.batches[course][0]);
    }
  };

  const handleBatchChange = (batch: string) => {
    setSelectedBatch(batch);
    setSelectedSubject('');
    setSelectedProject(null);
  };

  const handleSubjectChange = (subject: string) => {
    setSelectedSubject(subject);
    // Clear project when subject is selected
    setSelectedProject(null);
  };

  const handleProjectChange = (value: string) => {
    if (!value || !filtersData?.projects?.[selectedCourse]?.[selectedBatch]) {
      setSelectedProject(null);
    } else {
      const project = filtersData.projects[selectedCourse][selectedBatch].find(
        (p) => `${p.project_id}_${p.batch_project_id}` === value
      );
      setSelectedProject(project ?? null);
    }
    // Clear subject when project is selected
    setSelectedSubject('');
  };

  const handleSubmit = () => {
    if (selectedCourse && selectedBatch && (selectedSubject || selectedProject)) {
      // Save filters to session storage
      const filters = {
        course: selectedCourse,
        batch: selectedBatch,
        subject: selectedSubject || null,
        project: selectedProject ?? null
      };
      sessionStorage.setItem('dashboardFilters', JSON.stringify(filters));
      
      // Navigate to dashboard
      navigate('/coordinator/dashboard');
    }
  };

  if (loading) {
    return (
      <div className="filter-page">
        <div className="filter-page-loading-container">
          <div className="filter-page-loading-spinner"></div>
          <p>Loading filters...</p>
        </div>
      </div>
    );
  }

  // Check if data is empty
  const isEmpty = filtersData && 
    filtersData.courses.length === 0 && 
    Object.keys(filtersData.batches).length === 0 && 
    Object.keys(filtersData.subjects).length === 0 &&
    (!filtersData.projects || Object.keys(filtersData.projects).length === 0);

  return (
    <div className="filter-page">
      <div className="filter-page-container">
        <div className="filter-page-header">
          <h1>Welcome!</h1>
          {isEmpty ? (
            <p className='text-danger'>You have not been assigned a course and batch. Please contact the Exskilence team.</p>
          ) : (
            <p>Please select your course, batch, and project or subject to continue to the dashboard</p>
          )}
        </div>
        
        <div className="filter-page-form">
          <div className="filter-page-group">
            <label>Course</label>
            <select 
              className="filter-page-select"
              value={selectedCourse}
              onChange={(e) => handleCourseChange(e.target.value)}
            >
              <option value="">Select Course</option>
              {filtersData?.courses.map((course) => (
                <option key={course} value={course}>{course}</option>
              ))}
            </select>
          </div>
          
          <div className="filter-page-group">
            <label>Batch</label>
            <select 
              className="filter-page-select"
              value={selectedBatch}
              onChange={(e) => handleBatchChange(e.target.value)}
              disabled={!selectedCourse}
            >
              <option value="">Select Batch</option>
              {filtersData?.batches[selectedCourse]?.map((batch) => (
                <option key={batch} value={batch}>{batch}</option>
              ))}
            </select>
          </div>
          
          <div className="filter-page-group">
            <label>Project</label>
            <select 
              className="filter-page-select"
              value={selectedProject ? `${selectedProject.project_id}_${selectedProject.batch_project_id}` : ""}
              onChange={(e) => handleProjectChange(e.target.value)}
              disabled={!selectedBatch}
            >
              <option value="">Select Project</option>
              {filtersData?.projects?.[selectedCourse]?.[selectedBatch]?.map((project) => (
                <option key={`${project.project_id}_${project.batch_project_id}`} value={`${project.project_id}_${project.batch_project_id}`}>
                  {project.batch_project_name}
                </option>
              ))}
            </select>
          </div>

          <div className="d-flex align-items-center my-4">
            <div className="flex-grow-1 border-top"></div>
            <span className="px-3 text-muted text-uppercase small fw-medium">OR</span>
            <div className="flex-grow-1 border-top"></div>
          </div>

          <div className="filter-page-group">
            <label>Subject</label>
            <select 
              className="filter-page-select"
              value={selectedSubject}
              onChange={(e) => handleSubjectChange(e.target.value)}
              disabled={!selectedBatch}
            >
              <option value="">Select Subject</option>
              {filtersData?.subjects[selectedCourse]?.[selectedBatch]?.map((subject) => (
                <option key={subject} value={subject}>{subject}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="filter-page-actions">
          <button 
            className="filter-page-submit-btn"
            onClick={handleSubmit}
            disabled={!selectedCourse || !selectedBatch || (!selectedSubject && !selectedProject)}
          >
            Continue to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterPage; 